import React from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services';

const FormSidebar = ({ 
  isOpen, 
  close, 
  formName, 
  activeTab, 
  setActiveTab, 
  formData, 
  currentStep, 
  setCurrentStep, 
  completedSteps, 
  scrollToTop 
}) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/Login");
    } catch (error) {
      console.error("Logout failed:", error);
      navigate("/Login");
    }
  };

  return (
    <>
      <style>
        {`
          .sidebar-scrollbar {
            scrollbar-width: thin; /* Firefox */
            scrollbar-color: #F16745 #F1EBDB; /* Firefox */
          }

          /* Chrome, Edge, and Safari */
          .sidebar-scrollbar::-webkit-scrollbar {
            width: 8px;
            background: transparent;
          }

          .sidebar-scrollbar::-webkit-scrollbar-track {
            background: #F1EBDB;
            border-radius: 10px;
            margin: 8px 0;
          }

          .sidebar-scrollbar::-webkit-scrollbar-thumb {
            background: #F16745;
            border-radius: 10px;
            border: 2px solid #F1EBDB;
          }

          .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #e05635;
          }
        `}
      </style>
      <aside className={`fixed top-0 left-0 w-full lg:w-96 bg-white shadow-xl h-screen border-r border-2 overflow-y-auto transform transition-transform duration-300 ease-in-out sidebar-scrollbar ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 z-30`}>
        <div className="p-6 lg:pt-18 pt-20">
          <h2 className="text-xl font-semibold text-[#F16745] mb-6">
            {formName || "Booklet"}
          </h2>
          <nav>
            <ul className="space-y-2">
              {/* Dashboard Button - only show on mobile/tablet when menu icon is visible */}
              <li className="lg:hidden">
                <button
                  onClick={() => {
                    navigate("/");
                    close();
                  }}
                  className="group w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors text-gray-600 hover:bg-[#F1EBDB] hover:text-[#F16745] hover:ring-2 hover:ring-[#F16745] hover:ring-opacity-50"
                >
                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5 bg-gray-100 text-gray-600 group-hover:bg-[#F1EBDB] group-hover:text-[#F16745] group-hover:border-2 group-hover:border-[#F16745] transition-colors">
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-4 h-4"
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="9" />
                      <rect x="14" y="3" width="7" height="5" />
                      <rect x="14" y="12" width="7" height="9" />
                      <rect x="3" y="16" width="7" height="5" />
                    </svg>
                  </span>
                  <span className="font-medium leading-tight">
                    Dashboard
                  </span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    setActiveTab("video");
                    scrollToTop();
                    close();
                  }}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                    activeTab === "video"
                      ? "bg-[#F1EBDB] text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5 ${
                      activeTab === "video"
                        ? "bg-[#F1EBDB] text-[#F16745] border-2 border-[#F16745]"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-4 h-4"
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      stroke={activeTab === "video" ? "transparent" : "#F16745"}
                      strokeWidth="2"
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polygon points="5 3 19 12 5 21 5 3" fill={activeTab === "video" ? "#F16745" : "#F16745"} />
                    </svg>
                  </span>
                  <span className="font-medium leading-tight">
                    Introduction
                  </span>
                </button>
              </li>
              {formData.form_form_steps.map((step, index) => (
                <li key={step.id}>
                  <button
                    onClick={() => {
                      setActiveTab("form");
                      setCurrentStep(index);
                      scrollToTop();
                      close();
                    }}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                      activeTab === "form" && index === currentStep
                        ? "bg-[#F1EBDB] text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50"
                        : completedSteps.has(index)
                        ? "bg-green-50 text-green-700"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5
                      ${
                        activeTab === "form" && index === currentStep
                          ? "bg-[#F1EBDB] text-[#F16745] border-2 border-[#F16745]"
                          : completedSteps.has(index)
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {completedSteps.has(index) ? "✓" : index + 1}
                    </span>
                    <span className="font-medium leading-tight">
                      {step.title}
                    </span>
                  </button>
                </li>
              ))}
              
              {/* New Section 7: My PostScrypt Contacts */}
              <li>
                <button
                  onClick={() => {
                    setActiveTab("contacts");
                    scrollToTop();
                    close();
                  }}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                    activeTab === "contacts"
                      ? "bg-[#F1EBDB] text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5 ${
                      activeTab === "contacts"
                        ? "bg-[#F1EBDB] text-[#F16745] border-2 border-[#F16745]"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    7
                  </span>
                  <span className="font-medium leading-tight">
                    My PostScrypt Contacts
                  </span>
                </button>
              </li>

              {/* New Section 8: Changing Your Will */}
              <li>
                <button
                  onClick={() => {
                    setActiveTab("will");
                    scrollToTop();
                    close();
                  }}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                    activeTab === "will"
                      ? "bg-[#F1EBDB] text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5 ${
                      activeTab === "will"
                        ? "bg-[#F1EBDB] text-[#F16745] border-2 border-[#F16745]"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    8
                  </span>
                  <span className="font-medium leading-tight">
                    Changing Your Will
                  </span>
                </button>
              </li>

              {/* Logout Button - only show on mobile/tablet when menu icon is visible */}
              <li className="pt-4 border-t border-gray-200 mt-4 lg:hidden">
                <button
                  onClick={() => {
                    handleLogout();
                    close();
                  }}
                  className="group w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors text-gray-600 hover:bg-[#F1EBDB] hover:text-[#F16745] hover:ring-2 hover:ring-[#F16745] hover:ring-opacity-50"
                >
                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5 bg-gray-100 text-gray-600 group-hover:bg-[#F1EBDB] group-hover:text-[#F16745] group-hover:border-2 group-hover:border-[#F16745] transition-colors">
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-4 h-4"
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </span>
                  <span className="font-medium leading-tight">
                    Logout
                  </span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default FormSidebar; 